import React, {
  forwardRef,
  RefObject,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { Button } from 'react-bootstrap'
import NumericTextboxComponent from 'react-numeric-textbox'
import { ISearchReference } from 'shared/models/References'
import { CustomerDeliveryLocationsService } from 'services/CustomerDeliveryLocationsService'
import SelectieButtonEnum from 'shared/components/SelectieButtonEnum'
import {
  VestigingEnum,
  warehouseOptions,
  HistoryScope,
  WareHouseSearchOptions,
} from 'shared/models/Enum'
import { vestigingEnumToWareHouseSearchOptionsConvert } from 'shared/service/helpers/VestigingEnumToWareHouseSearchOptionsConvert'
import { DeliveryLocationsOverviewModel } from 'shared/models/CutomerDeliveryLocations/DeliveryLocationsOverviewModel'

interface IProps {
  onError?: (error: string) => void
  onInputChanged?: () => void
  onLoading?: (isLoading: boolean) => void
  onResult?: (result: DeliveryLocationsOverviewModel) => void
  clearResults?: () => void
  returnedAfterAction: boolean
  warehouse: VestigingEnum
  service: CustomerDeliveryLocationsService
}

function SearchCustomers(props: IProps, ref: RefObject<ISearchReference>) {
  const [customerNumber, setCustomerNumber] = useState<number>(null)
  const [agreementReference, setAgreementReference] = useState<string>('')
  const [locationName, setLocationName] = useState<string>('')
  const [
    sevenDayHistoryRadioButtonChecked,
    setSevenDayHistoryRadioButtonChecked,
  ] = useState(false)
  const [allHistoryRadioButtonChecked, setAllHistoryRadioButtonChecked] =
    useState(false)

  useImperativeHandle(ref, () => ({
    search: () => {
      searchCustomer()
    },
  }))

  useEffect(() => {
    if (props.returnedAfterAction) {
      searchCustomer()
    }
  }, [props.returnedAfterAction])

  const re = /^[0-9\b]+$/

  const updateCustomer = x => {
    if (re.test(x) || x === null) {
      setAgreementReference('')
      setLocationName('')
      setCustomerNumber(x)
      if (!!props.onInputChanged) {
        props.onInputChanged()
      }
    }
  }

  const updateReference = x => {
    setAgreementReference(x.target.value)
    setCustomerNumber(null)
    setLocationName('')
    if (!!props.onInputChanged) {
      props.onInputChanged()
    }
  }

  const updateLocationName = x => {
    setLocationName(x.target.value)
    setAgreementReference('')
    setCustomerNumber(null)
    if (!!props.onInputChanged) {
      props.onInputChanged()
    }
  }

  const onChangeSevenDayHistoryRadioButtonChecked = () => {
    const newValue: boolean = !sevenDayHistoryRadioButtonChecked
    setSevenDayHistoryRadioButtonChecked(newValue)
    if (newValue && allHistoryRadioButtonChecked) {
      setAllHistoryRadioButtonChecked(false)
    }
  }

  const onChangeAllHistoryRadioButtonChecked = () => {
    const newValue: boolean = !allHistoryRadioButtonChecked
    setAllHistoryRadioButtonChecked(newValue)
    if (newValue && sevenDayHistoryRadioButtonChecked) {
      setSevenDayHistoryRadioButtonChecked(false)
    }
  }

  const searchCustomer = async () => {
    props.clearResults()
    if (!!customerNumber && `${customerNumber}`.length > 9 && !!props.onError) {
      props.onError(`${customerNumber} is geen geldig klantnummer.`)
    } else if (
      (!!customerNumber && customerNumber > 0) ||
      (!!agreementReference && agreementReference.length > 0) ||
      (!!locationName && locationName.length > 0)
    ) {
      if (!!props.onLoading) {
        props.onLoading(true)
      }

      const result = await props.service.searchCustomers(
        customerNumber,
        agreementReference,
        locationName,
        props.warehouse,
        getSelectionExpiryDate()
      )
      if (result.success && !!props.onResult) {
        props.onResult(result.payLoad)
      } else if (!!props.onError) {
        props.onError(result.errorMessage)
      }

      if (!!props.onLoading) {
        props.onLoading(false)
      }
    } else if (!!props.onError) {
      props.onError(
        'Kies eerst een (geldig) klantnummer en/of afspraakreferentie'
      )
    }
  }

  const getSelectionExpiryDate = (): HistoryScope => {
    var now = new Date()
    if (!sevenDayHistoryRadioButtonChecked && !allHistoryRadioButtonChecked) {
      return HistoryScope.Default
    } else if (sevenDayHistoryRadioButtonChecked) {
      return HistoryScope.History7Day
    } else {
      return HistoryScope.HistoryAll
    }
  }

  const searchCustomerIfEnter = (e: string) => {
    if (e === 'Enter') {
      searchCustomer()
    }
  }

  return (
    <table className='selectie'>
      <thead>
        <tr>
          <th className='selectie-label'>Klantnummer (CKN):</th>
          <th className='selectie-label'>AfleverAfspraakreferentie:</th>
          <th className='selectie-label'>Locatie Naam (Route-to/Ship-to):</th>
          <th className='selectie-label'>Vestiging:</th>
          <th className='selectie-label'>Incl. historie:</th>
          <th className='selectie-label'></th>
        </tr>
      </thead>
      <tbody>
        <tr onKeyUp={e => searchCustomerIfEnter(e.key)}>
          <td>
            <NumericTextboxComponent
              min={0}
              max={999999}
              decimals={0}
              format={'0'}
              value={customerNumber}
              onChange={updateCustomer}
            />
          </td>
          <td>
            <input value={agreementReference} onChange={updateReference} />
          </td>
          <td>
            <input value={locationName} onChange={updateLocationName} />
          </td>
          <td>{VestigingEnum[props.warehouse]}</td>
          <td>
            <div className='text-right font-weight-bold'>
              <label>7d</label>
              <input
                style={{ margin: 6, marginRight: 6, textAlign: 'center' }}
                type='checkbox'
                value='1'
                checked={sevenDayHistoryRadioButtonChecked}
                onChange={() => onChangeSevenDayHistoryRadioButtonChecked()}
              />
              <label>Totaal </label>
              <input
                style={{ margin: 6, marginRight: 12, textAlign: 'center' }}
                type='checkbox'
                value='1'
                checked={allHistoryRadioButtonChecked}
                onChange={() => onChangeAllHistoryRadioButtonChecked()}
              />
            </div>
          </td>
          <td>
            <div className='row no-gutters'>
              <div className='col'></div>
              <div className='col-auto'>
                <Button
                  variant='primary'
                  disabled={
                    !customerNumber && !agreementReference && !locationName
                  }
                  onClick={searchCustomer}
                >
                  Zoek
                </Button>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  )
}
export default forwardRef(SearchCustomers)
